import { createSlice } from "@reduxjs/toolkit";

import { getStaffAudit, getStaffAuditDiscrepancies } from "./staffAPI";
import { staffErrorMessage } from "./staffSlice";

export const AUDIT_ACTIONS = [
  { value: "staff.create", label: "Account created" },
  { value: "staff.roles.grant", label: "Roles granted" },
  { value: "staff.roles.revoke", label: "Role revoked" },
  { value: "staff.primary-role.change", label: "Primary role changed" },
  { value: "staff.enable", label: "Account enabled" },
  { value: "staff.disable", label: "Account disabled" },
  { value: "staff.invitation.resend", label: "Invitation resent" },
  { value: "staff.password.reset", label: "Password reset sent" },
  { value: "role.create", label: "Role created" },
  { value: "role.permissions.change", label: "Role permissions changed" },
  { value: "role.delete", label: "Role deleted" },
];

const initialState = {
  records: [],
  discrepancies: [],
  status: "idle",
  discrepanciesStatus: "idle",
  error: "",
  page: 0,
  size: 50,
  filters: { actor: "", target: "", actorType: "", action: "" },
};

export const staffAuditSlice = createSlice({
  name: "staffAudit",
  initialState,
  reducers: {
    setRecords: (state, action) => {
      state.records = action.payload;
      state.status = "succeeded";
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setDiscrepancies: (state, action) => {
      state.discrepancies = action.payload;
      state.discrepanciesStatus = "succeeded";
    },
    setDiscrepanciesStatus: (state, action) => {
      state.discrepanciesStatus = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 0;
    },
  },
});

export const {
  setRecords,
  setStatus,
  setDiscrepancies,
  setDiscrepanciesStatus,
  setError,
  setPage,
  setFilters,
} = staffAuditSlice.actions;

export const selectStaffAudit = (state) => state.staffAudit;

export const handleGetStaffAudit = () => async (dispatch, getState) => {
  const { page, size, filters } = getState().staffAudit;
  dispatch(setStatus("loading"));
  dispatch(setError(""));
  try {
    const response = await getStaffAudit({ ...filters, page, size });
    dispatch(setRecords(response.data));
  } catch (error) {
    dispatch(setError(staffErrorMessage(error)));
    dispatch(setStatus("failed"));
  }
};

/**
 * Loaded separately from the page it sits above, and never filtered.
 *
 * A `PENDING` record means a privilege change was authorised and its outcome was never written, so
 * nobody knows whether it landed. Folding those into the main list would let the filters hide them,
 * which is the one thing this view must not do.
 */
export const handleGetStaffAuditDiscrepancies = () => async (dispatch) => {
  dispatch(setDiscrepanciesStatus("loading"));
  try {
    const response = await getStaffAuditDiscrepancies();
    dispatch(setDiscrepancies(response.data));
  } catch (error) {
    dispatch(setDiscrepanciesStatus("failed"));
    dispatch(setError(staffErrorMessage(error)));
  }
};

export default staffAuditSlice.reducer;
